import { useCallback, useRef, useState } from 'react'
import { Message } from '@arco-design/web-react'
import type { SecurityProof } from '@/api/verify'

export interface SecureActionOptions {
  /** 敏感操作 scope，例如 channel.key.read；后端按它签发凭据 */
  scope: string
  /** 与 scope 绑定的上下文，例如 { channel_id: 3 } */
  context?: Record<string, unknown>
}

/** 传给子组件的签名：runSecure(apiCall, { scope, context }) */
export type SecureRunner = <T>(
  apiCall: (proof?: string) => Promise<T>,
  options: SecureActionOptions
) => Promise<T>

interface PendingCall {
  call: (proof?: string) => Promise<unknown>
  resolve: (value: unknown) => void
  reject: (error: unknown) => void
}

/**
 * 封装敏感操作调用流程：
 * 1) 先执行 apiCall（无凭据）
 * 2) 后端返回 403 SECURITY_PROOF_REQUIRED 时弹验证框
 * 3) 拿到 proof_token 后带 `X-Security-Proof` 头重试
 *
 * 用法：await runSecure((proof) => getChannelKeyApi(id, proof), { scope, context })
 */
export function useSecureVerification() {
  const [verifyVisible, setVerifyVisible] = useState(false)
  const [verifyScope, setVerifyScope] = useState('')
  const [verifyContext, setVerifyContext] = useState<Record<string, unknown> | undefined>(undefined)
  const pendingRef = useRef<PendingCall | null>(null)

  const runSecure = useCallback(
    async <T,>(apiCall: (proof?: string) => Promise<T>, options: SecureActionOptions): Promise<T> => {
      try {
        return await apiCall()
      } catch (err: unknown) {
        const e = err as {
          response?: { status?: number; data?: { code?: string; message?: string } }
          message?: string
        }
        const code = e?.response?.data?.code ?? ''
        const msg = e?.response?.data?.message ?? e?.message ?? ''
        const needProof =
          e?.response?.status === 403 &&
          (code.startsWith('SECURITY_') || msg.includes('verification') || msg.includes('验证'))
        if (!needProof) throw err

        setVerifyScope(options.scope)
        setVerifyContext(options.context)
        return new Promise<T>((resolve, reject) => {
          pendingRef.current = {
            call: apiCall as (proof?: string) => Promise<unknown>,
            resolve: resolve as (value: unknown) => void,
            reject,
          }
          setVerifyVisible(true)
        })
      }
    },
    []
  )

  /** 验证成功：带凭据重放待执行的操作 */
  const handleSuccess = useCallback(async (proof: SecurityProof) => {
    setVerifyVisible(false)
    const pending = pendingRef.current
    pendingRef.current = null
    if (!pending) return
    try {
      pending.resolve(await pending.call(proof.proof_token))
    } catch (err) {
      Message.error(err instanceof Error ? err.message : '操作失败')
      pending.reject(err)
    }
  }, [])

  const handleCancel = useCallback(() => {
    setVerifyVisible(false)
    const pending = pendingRef.current
    pendingRef.current = null
    pending?.reject(new Error('用户取消了二次验证'))
  }, [])

  return {
    runSecure,
    verifyVisible,
    verifyScope,
    verifyContext,
    handleSuccess,
    handleCancel,
  }
}
